import { type WorkflowSummary } from '@opallabs/concepts'
import { type icons } from '@opallabs/constellation'

/**
 * Every object family a calendar surface can describe. `story` and `block` are
 * part of the contract from day one so adapters never have to reshape their
 * payload, even where the surface does not draw them yet.
 */
export type CalendarItemKind = 'moment' | 'content' | 'story' | 'block'

/**
 * A date range in which the item is live on its channel. Present on content
 * whose in-market window is known.
 */
export type CalendarItemInMarket = Readonly<{
  /** ISO 8601 date, day precision. */
  start: string
  /** ISO 8601 date, day precision. Inclusive. */
  end: string
  /**
   * True when the content has no takedown of its own and the window ends when
   * its moment does. `end` is then the moment's end, and moves with the moment
   * rather than with the content.
   */
  followsMoment?: boolean
}>

/**
 * The single shape every calendar surface renders and every filter reads.
 * Adapters translate their own API payloads into this; nothing here knows about
 * moments, content, JSON:API or the router.
 *
 * Dates are ISO 8601 strings at day precision because a surface only ever lays
 * items out by day. Times stay in `time` as display text, already formatted for
 * the viewer's locale and zone by whoever built the item.
 */
export type CalendarItem = Readonly<{
  id: string
  kind: CalendarItemKind
  title: string
  /** ISO 8601 date, day precision. */
  start: string
  /** ISO 8601 date, day precision. Inclusive: a one-day item has `start === end`. */
  end: string
  /**
   * The scheduled instant, ISO 8601 with a time. Orders the chips inside a day
   * and carries the clock time a reschedule has to preserve. Absent when no time
   * was chosen; those items sort last.
   *
   * @note `time` is display text and cannot be sorted — "4:00 PM" sorts before
   * "9:00 AM" — so ordering always reads this instead.
   */
  startsAt?: string
  /** Display time for the chip, pre-formatted. Absent when no time was chosen. */
  time?: string
  statusId?: string
  /** CSS color for the status bar on a content chip. */
  statusColor?: string
  labelIds?: string[]
  /** CSS color chosen by a person (label, moment color). Drives bar tinting. */
  primaryColor?: string
  channelId?: string
  accountId?: string
  /**
   * The id the product's own pages are routed by, and that the v2 endpoints are
   * keyed by, where it differs from `id`. A host builds its links from it, and
   * an action that can only go through v2 — renaming or deleting a piece of
   * content — cannot run at all without it.
   */
  legacyId?: string
  /** Parent moment of a content item. Drives color inheritance. */
  parentId?: string
  /** `legacyId` of the parent moment, which a link to the content also names. */
  parentLegacyId?: string
  storyId?: string
  boardIds?: string[]
  isPrivate?: boolean
  /** Number of children, shown as a badge on a moment bar. */
  childCount?: number
  inMarket?: CalendarItemInMarket
  /** Navigation target for the item, when one exists. */
  href?: string
  /**
   * Extra text quickfind reads, on top of the title. The account name goes here:
   * quickfind matches it in the design, and nothing in this package can turn
   * `accountId` into a name.
   */
  searchText?: string
}>

/**
 * The state of the workflow running over each item, for the items that have
 * one. Nothing in `@opallabs/filters` fetches these: the surface reads them
 * and hands them in, the same way it hands in every other option list.
 *
 * Keys come from `workflowSummaryKey`, so one map answers for every kind. An
 * item with no entry has no workflow, which is a state of its own — but only
 * once `isLoaded` is true. Until then nothing is known, and the Status filter
 * says nothing about the kinds it reads from here.
 */
export type CalendarWorkflowSummaries = Readonly<{
  byKey: ReadonlyMap<string, WorkflowSummary>
  isLoaded: boolean
}>

export type CalendarFilterMode = 'include' | 'exclude'

/**
 * Whose choice Include or Exclude is.
 *
 * Almost every category makes it once, for the whole of itself: Channels
 * matches what it holds, or it hides it. Labels and Approval status make it row
 * by row, because a person filtering by them is asking for one row and against
 * another in the same breath — "the Paid campaigns, but not the ones for
 * Education"; "the content that is approved, but none of the moments still
 * waiting".
 */
export type CalendarModeScope = 'category' | 'parent'

/**
 * What one category holds.
 *
 * `mode` says which of the two lists the category filters by: it matches what
 * it holds, or it hides it. The other list holds nothing but carve-outs —
 * values under a row the filtering list already holds, which is how "this
 * channel except that account" is said either way round.
 *
 * A category whose mode belongs to each parent holds no `mode` of its own. The
 * two lists say it set by set instead: see `parentFilterMode`.
 */
export type CalendarCategoryFilter = Readonly<{
  mode?: CalendarFilterMode
  include: readonly string[]
  exclude: readonly string[]
  /**
   * What a person has said about a parent that holds nothing yet.
   *
   * A parent with a tick says which way round it is read by which list the
   * tick is in, and that is the only answer worth keeping. A parent with no
   * ticks has nowhere to say it, so the answer waits here until the first tick
   * lands in the list it names — and then the lists are the truth again and
   * the entry goes. It is never written to the address for the same reason a
   * question nobody has answered is not worth sharing.
   */
  parentModes?: Readonly<Record<string, CalendarFilterMode>>
}>

/**
 * A window of days, either end of it open. Dates are ISO 8601 at day precision,
 * the way every calendar item carries them.
 */
export type CalendarDateRangeFilter = Readonly<{ from?: string; to?: string }>

/** Everything a person has chosen to filter by, category by category. */
export type CalendarFilterState = Readonly<{
  cats: Readonly<Record<string, CalendarCategoryFilter>>
  /**
   * The days an item has to be live on. It is not a list of values, so it is
   * kept beside the categories rather than inside them.
   */
  live?: CalendarDateRangeFilter
}>

/**
 * The heading a set of options sits under in the drawer: a label set over its
 * labels, a channel over its accounts.
 *
 * Matching never reads a group. A heading is only choosable when the category
 * offers an option of the same id — a channel is an option and the heading its
 * accounts sit under — and then it is the option that is chosen, not the group.
 * It is shaped like an option so the same `renderOptionIcon` can draw a mark
 * beside it, as the grid draws one on a chip.
 */
export type CalendarFilterOptionGroup = Readonly<{
  id: string
  label: string
}>

export type CalendarFilterOption = Readonly<{
  id: string
  label: string
  /** CSS color for the dot in front of the option and its token. */
  color?: string
  /**
   * The heading this option belongs under. Presentation only: the options of a
   * category are one flat list to everything that filters, so a group can be
   * added or taken away without changing what any filter means.
   */
  group?: CalendarFilterOptionGroup
  /**
   * The item values this option stands for, kind by kind. Leave it out when the
   * option id is the value itself, which is how most categories work: an item
   * carries the very id the option offers.
   *
   * Fill it in when one option has to mean different things to different kinds
   * — Approved is a workflow status on content and a workflow summary on a
   * moment. A kind the option says nothing about matches nothing.
   */
  values?: Readonly<Partial<Record<CalendarItemKind, readonly string[]>>>
}>

/**
 * One row in the filter drawer. Callers build the list — the drawer never
 * fetches option data — so an adapter can register categories the package knows
 * nothing about, such as the custom fields of a block.
 */
export type CalendarFilterCategory = Readonly<{
  /** Stable: it is the name this category's values get in the URL. */
  id: string
  label: string
  icon: keyof typeof icons.gem
  /**
   * The item kinds this category can speak about. The drawer shows a category
   * only when the calendar shows at least one of these kinds.
   *
   * A kind left out is not waved through: it holds no value here, so only its
   * moment or its content can keep it on the calendar. That is what makes
   * filtering by channel — which only content carries — hide the moments whose
   * content does not match.
   */
  appliesTo: readonly CalendarItemKind[]
  options: readonly CalendarFilterOption[]
  /**
   * What a person picks in this category, where it is not a list of values. A
   * date range offers two dates instead, so it holds no options, matches on the
   * item's own window rather than through `getValues`, and offers no Include or
   * Exclude — a window is a window either way.
   */
  picker?: 'dateRange'
  /**
   * Whose choice Include or Exclude is in this category. Left out, it is the
   * category's own, which is what every category but Labels wants.
   */
  modeScope?: CalendarModeScope
  /**
   * Whether an item has to match every parent a person matched, rather than
   * any one of them. Values under one parent are still ORed.
   *
   * Only Labels wants it: an item carries a label from many sets at once, and
   * asking for Global and for the Social team means both. Leave it off where
   * an item holds one value — one account, one status — or no item could ever
   * match two parents.
   */
  matchesEveryParent?: boolean
  /**
   * Whether a row that stands for the whole list is worth offering. Leave it
   * off where choosing everything says nothing: Privacy holds two options, and
   * choosing both of them is the same as filtering by nothing at all.
   */
  shouldOfferSelectAll?: boolean
  /**
   * The item's values in this category.
   *
   * An empty list and `undefined` are not the same answer. An empty list is
   * known: the item holds nothing in this category, so it matches nothing and
   * only its moment or its content can keep it on the calendar. `undefined`
   * means the category cannot say yet — a lookup it needs is still on its way —
   * so it has no opinion and the item passes until the answer arrives.
   */
  getValues: (item: CalendarItem) => readonly string[] | undefined
}>

export const EMPTY_CALENDAR_FILTERS: CalendarFilterState = { cats: {} }

export const EMPTY_CATEGORY_FILTER: CalendarCategoryFilter = {
  exclude: [],
  include: [],
  mode: 'include',
}
